import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiUpload,
  FiVideo,
  FiImage,
  FiX,
  FiCheckCircle,
  FiAlertTriangle,
  FiXCircle,
  FiSlash,
} from 'react-icons/fi';

import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import PlatformBadge, { getPlatform } from '../PlatformBadge/PlatformBadge';
import {
  VIDEO_MIME,
  MAX_VIDEO_BYTES,
  MIN_DURATION_SEC,
  MAX_DURATION_SEC,
  ASPECT_RATIO_TARGET,
  ASPECT_RATIO_TOLERANCE,
  THUMB_MAX_BYTES,
  THUMB_MIMES,
  CAPTION_LIMITS,
  YT_TITLE_MAX,
  READINESS_LABELS,
  minCaptionLimitFor,
} from '../../Utilities/uploadConstants';
import PlatformFields from './PlatformFields';
import './UploadVideoModal.css';

const READINESS_ICON = {
  ready: <FiCheckCircle className="urm-icon-ready" />,
  needs_upload_scope: <FiAlertTriangle className="urm-icon-warn" />,
  token_expired: <FiXCircle className="urm-icon-error" />,
  not_connected: <FiSlash className="urm-icon-muted" />,
};

const defaultPlatformFields = (platform) => {
  switch (platform) {
    case 'youtube':
      return { title: '', visibility: 'public', made_for_kids: false, tags: '' };
    case 'tiktok':
      return {
        privacy_level: 'PUBLIC_TO_EVERYONE',
        disable_comment: false,
        disable_duet: false,
        disable_stitch: false,
        brand_content_toggle: false,
        brand_organic_toggle: false,
      };
    case 'instagram':
      return { share_to_feed: true, cover_frame_ms: '' };
    case 'facebook':
      return { description: '' };
    default:
      return {};
  }
};

const probeVideoMetadata = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () => {
      const meta = {
        durationSec: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
      };
      URL.revokeObjectURL(url);
      resolve(meta);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    video.src = url;
  });

const UploadVideoModal = ({
  open,
  accountId,
  readiness,
  onClose,
  onSubmit,
  onEnableUploads,
  onReconnect,
  onLinkPlatform,
}) => {
  const [videoFile, setVideoFile] = useState(null);
  const [videoMeta, setVideoMeta] = useState(null);
  const [videoError, setVideoError] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [thumbError, setThumbError] = useState(null);
  const [selectedLoginIds, setSelectedLoginIds] = useState([]);
  const [captionMode, setCaptionMode] = useState('common');
  const [commonCaption, setCommonCaption] = useState('');
  const [perPlatformCaptions, setPerPlatformCaptions] = useState({});
  const [platformFields, setPlatformFields] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dropRef = useRef(null);
  const videoInputRef = useRef(null);
  const thumbInputRef = useRef(null);

  const reset = useCallback(() => {
    setVideoFile(null);
    setVideoMeta(null);
    setVideoError(null);
    setThumbFile(null);
    setThumbError(null);
    setSelectedLoginIds([]);
    setCaptionMode('common');
    setCommonCaption('');
    setPerPlatformCaptions({});
    setPlatformFields({});
    setSubmitting(false);
  }, []);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  // Keep per-platform state in sync with selection.
  useEffect(() => {
    setPerPlatformCaptions((prev) => {
      const next = { ...prev };
      for (const lid of selectedLoginIds) {
        if (!(lid in next)) next[lid] = commonCaption;
      }
      for (const lid of Object.keys(next)) {
        if (!selectedLoginIds.includes(lid)) delete next[lid];
      }
      return next;
    });
    setPlatformFields((prev) => {
      const next = { ...prev };
      for (const lid of selectedLoginIds) {
        const login = readiness?.logins?.find((l) => l.login_id === lid);
        if (!login) continue;
        if (!(lid in next)) next[lid] = defaultPlatformFields(login.platform);
      }
      for (const lid of Object.keys(next)) {
        if (!selectedLoginIds.includes(lid)) delete next[lid];
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLoginIds, readiness]);

  const selectedLogins = useMemo(
    () =>
      (readiness?.logins || []).filter((l) => selectedLoginIds.includes(l.login_id)),
    [readiness, selectedLoginIds]
  );

  const selectedPlatforms = useMemo(
    () => selectedLogins.map((l) => l.platform),
    [selectedLogins]
  );

  const commonCaptionLimit = minCaptionLimitFor(selectedPlatforms);

  const handleVideoFile = async (file) => {
    setVideoError(null);
    setVideoFile(null);
    setVideoMeta(null);
    if (!file) return;
    if (file.type !== VIDEO_MIME) {
      setVideoError('Must be an MP4 file.');
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      setVideoError('File is larger than 200 MB.');
      return;
    }
    const meta = await probeVideoMetadata(file);
    if (!meta) {
      setVideoError("Couldn't read the video's metadata.");
      return;
    }
    setVideoFile(file);
    setVideoMeta(meta);
  };

  const handleThumbFile = (file) => {
    setThumbError(null);
    setThumbFile(null);
    if (!file) return;
    if (!THUMB_MIMES.includes(file.type)) {
      setThumbError('Thumbnail must be JPG or PNG.');
      return;
    }
    if (file.size > THUMB_MAX_BYTES) {
      setThumbError('Thumbnail must be under 2 MB.');
      return;
    }
    setThumbFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleVideoFile(file);
  };

  // ----- Per-target validation -----
  const videoDurationOk =
    videoMeta &&
    videoMeta.durationSec >= MIN_DURATION_SEC &&
    videoMeta.durationSec <= MAX_DURATION_SEC;

  const videoAspectOk =
    videoMeta &&
    videoMeta.width > 0 &&
    Math.abs(videoMeta.width / videoMeta.height - ASPECT_RATIO_TARGET) <=
      ASPECT_RATIO_TOLERANCE;

  const platformCaptionError = (platform, caption) => {
    const limit = CAPTION_LIMITS[platform];
    if (caption.length > limit) return `Over ${platform} limit (${limit}).`;
    return null;
  };

  const requiredFieldError = (login) => {
    const f = platformFields[login.login_id] || {};
    if (login.platform === 'youtube') {
      const title = (f.title || '').trim();
      if (!title) return 'Title is required.';
      if (title.length > YT_TITLE_MAX) return `Title max ${YT_TITLE_MAX} chars.`;
    }
    if (login.platform === 'tiktok' && !f.privacy_level) {
      return 'Privacy level is required.';
    }
    return null;
  };

  const canSubmit = useMemo(() => {
    if (!videoFile || !videoMeta || !videoDurationOk || !videoAspectOk) return false;
    if (selectedLoginIds.length === 0) return false;
    if (captionMode === 'common' && commonCaption.length > commonCaptionLimit) return false;
    for (const login of selectedLogins) {
      if (requiredFieldError(login)) return false;
      if (captionMode === 'per_platform') {
        const cap = perPlatformCaptions[login.login_id] ?? '';
        if (platformCaptionError(login.platform, cap)) return false;
      } else if (platformCaptionError(login.platform, commonCaption)) {
        // shouldn't be reachable due to commonCaptionLimit, but keep it honest
        return false;
      }
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    videoFile,
    videoMeta,
    videoDurationOk,
    videoAspectOk,
    selectedLoginIds,
    selectedLogins,
    captionMode,
    commonCaption,
    commonCaptionLimit,
    perPlatformCaptions,
    platformFields,
  ]);

  const toggleLogin = (login) => {
    if (login.status !== 'ready') return;
    setSelectedLoginIds((prev) =>
      prev.includes(login.login_id)
        ? prev.filter((id) => id !== login.login_id)
        : [...prev, login.login_id]
    );
  };

  const handleCaptionModeToggle = (nextOn) => {
    const nextMode = nextOn ? 'per_platform' : 'common';
    setCaptionMode(nextMode);
    if (nextMode === 'per_platform') {
      // Seed per-platform captions with common.
      setPerPlatformCaptions((prev) => {
        const next = { ...prev };
        for (const lid of selectedLoginIds) {
          if (!next[lid]) next[lid] = commonCaption;
        }
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit({
        videoFile,
        thumbFile,
        durationSec: Math.round(videoMeta.durationSec),
        captionMode,
        commonCaption,
        targets: selectedLogins.map((login) => ({
          login_id: login.login_id,
          platform: login.platform,
          caption_override:
            captionMode === 'per_platform'
              ? perPlatformCaptions[login.login_id] ?? ''
              : null,
          platform_fields: normalizePlatformFields(
            login.platform,
            platformFields[login.login_id] || defaultPlatformFields(login.platform),
            commonCaption
          ),
        })),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upload video"
      width={620}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            icon={<FiUpload />}
          >
            {submitting ? 'Uploading…' : 'Upload & schedule'}
          </Button>
        </>
      }
    >
      {/* ---------- Video file picker ---------- */}
      <div className="uvm-section">
        <div className="uvm-section-label">Video file</div>
        <div
          ref={dropRef}
          className={`uvm-drop ${dragActive ? 'uvm-drop-active' : ''} ${
            videoFile ? 'uvm-drop-has-file' : ''
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          onClick={() => videoInputRef.current?.click()}
        >
          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4"
            hidden
            onChange={(e) => handleVideoFile(e.target.files?.[0])}
          />
          {videoFile ? (
            <div className="uvm-video-summary">
              <FiVideo className="uvm-video-summary-icon" />
              <div className="uvm-video-summary-text">
                <div className="uvm-video-summary-name">{videoFile.name}</div>
                <div className="uvm-video-summary-meta">
                  {(videoFile.size / (1024 * 1024)).toFixed(1)} MB
                  {videoMeta &&
                    ` · ${Math.round(videoMeta.durationSec)}s · ${videoMeta.width}×${
                      videoMeta.height
                    }`}
                </div>
              </div>
              <button
                className="uvm-icon-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setVideoFile(null);
                  setVideoMeta(null);
                }}
                aria-label="Remove video"
              >
                <FiX />
              </button>
            </div>
          ) : (
            <div className="uvm-drop-prompt">
              <FiUpload />
              <div>
                <strong>Drop an MP4 here</strong> or click to choose
                <div className="uvm-drop-hint">9:16, ≤ 200 MB, ≤ 15 min</div>
              </div>
            </div>
          )}
        </div>
        {videoError && <div className="uvm-error-inline">{videoError}</div>}
        {videoMeta && !videoDurationOk && (
          <div className="uvm-error-inline">
            Duration must be between {MIN_DURATION_SEC}s and {MAX_DURATION_SEC}s.
          </div>
        )}
        {videoMeta && !videoAspectOk && (
          <div className="uvm-error-inline">
            Aspect ratio must be 9:16 (got {videoMeta.width}×{videoMeta.height}).
          </div>
        )}
      </div>

      {/* ---------- Platform selector ---------- */}
      <div className="uvm-section">
        <div className="uvm-section-label">Post to</div>
        {(!readiness || readiness.logins.length === 0) && (
          <div className="uvm-empty">No platforms linked yet.</div>
        )}
        <div className="uvm-platform-list">
          {readiness?.logins?.map((login) => {
            const p = getPlatform(login.platform);
            const selected = selectedLoginIds.includes(login.login_id);
            const disabled = login.status !== 'ready';
            return (
              <div
                key={login.login_id}
                className={`uvm-platform-row ${selected ? 'selected' : ''} ${
                  disabled ? 'disabled' : ''
                }`}
                onClick={() => toggleLogin(login)}
              >
                <div className="uvm-platform-left">
                  <input
                    type="checkbox"
                    checked={selected}
                    disabled={disabled}
                    onChange={() => toggleLogin(login)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <PlatformBadge platform={login.platform} size="sm" />
                  <div>
                    <div className="uvm-platform-name">{p?.label || login.platform}</div>
                    <div className="uvm-platform-sub">{login.platform_username}</div>
                  </div>
                </div>
                <div className="uvm-platform-right">
                  <span className={`uvm-status uvm-status-${login.status}`}>
                    {READINESS_ICON[login.status]}
                    {READINESS_LABELS[login.status]}
                  </span>
                  {login.status === 'needs_upload_scope' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEnableUploads?.(login);
                      }}
                    >
                      Enable uploads
                    </Button>
                  )}
                  {login.status === 'token_expired' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        onReconnect?.(login);
                      }}
                    >
                      Reconnect
                    </Button>
                  )}
                  {login.status === 'not_connected' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        onLinkPlatform?.(login);
                      }}
                    >
                      Connect first
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ---------- Caption ---------- */}
      {selectedLoginIds.length > 0 && (
        <div className="uvm-section">
          <div className="uvm-section-label uvm-section-label-row">
            Caption
            <label className="uvm-switch">
              <input
                type="checkbox"
                checked={captionMode === 'per_platform'}
                onChange={(e) => handleCaptionModeToggle(e.target.checked)}
              />
              <span>Customize per platform</span>
            </label>
          </div>

          {captionMode === 'common' ? (
            <>
              <textarea
                className="uvm-textarea"
                rows={4}
                value={commonCaption}
                onChange={(e) => setCommonCaption(e.target.value)}
                placeholder="Write a caption everyone will see…"
              />
              <div
                className={`uvm-char-counter ${
                  commonCaption.length > commonCaptionLimit ? 'uvm-over' : ''
                }`}
              >
                {commonCaption.length} / {commonCaptionLimit}
              </div>
            </>
          ) : (
            <div className="uvm-per-platform-captions">
              {selectedLogins.map((login) => {
                const cap = perPlatformCaptions[login.login_id] ?? '';
                const limit = CAPTION_LIMITS[login.platform];
                const over = cap.length > limit;
                return (
                  <div key={login.login_id} className="uvm-pp-caption">
                    <div className="uvm-pp-caption-head">
                      <PlatformBadge platform={login.platform} size="sm" />
                      <span className="uvm-pp-caption-user">
                        {login.platform_username}
                      </span>
                      <span className={`uvm-char-counter ${over ? 'uvm-over' : ''}`}>
                        {cap.length} / {limit}
                      </span>
                    </div>
                    <textarea
                      className="uvm-textarea"
                      rows={3}
                      value={cap}
                      onChange={(e) =>
                        setPerPlatformCaptions((prev) => ({
                          ...prev,
                          [login.login_id]: e.target.value,
                        }))
                      }
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ---------- Per-platform field sections ---------- */}
      {selectedLogins.map((login) => (
        <PlatformFields
          key={login.login_id}
          login={login}
          fields={platformFields[login.login_id] || defaultPlatformFields(login.platform)}
          error={requiredFieldError(login)}
          onChange={(patch) =>
            setPlatformFields((prev) => ({
              ...prev,
              [login.login_id]: { ...prev[login.login_id], ...patch },
            }))
          }
        />
      ))}

      {/* ---------- Thumbnail ---------- */}
      {selectedLoginIds.length > 0 && (
        <div className="uvm-section">
          <div className="uvm-section-label">Thumbnail (optional)</div>
          {thumbFile ? (
            <div className="uvm-thumb-summary">
              <FiImage />
              <span>{thumbFile.name}</span>
              <button
                className="uvm-icon-btn"
                onClick={() => setThumbFile(null)}
                aria-label="Remove thumbnail"
              >
                <FiX />
              </button>
            </div>
          ) : (
            <button
              className="uvm-secondary-btn"
              onClick={() => thumbInputRef.current?.click()}
            >
              <FiImage />
              Choose thumbnail
            </button>
          )}
          <input
            ref={thumbInputRef}
            type="file"
            accept="image/jpeg,image/png"
            hidden
            onChange={(e) => handleThumbFile(e.target.files?.[0])}
          />
          {thumbError && <div className="uvm-error-inline">{thumbError}</div>}
        </div>
      )}
    </Modal>
  );
};

// Convert our UI-shaped field state into the wire format the backend expects.
const normalizePlatformFields = (platform, fields, commonCaption) => {
  switch (platform) {
    case 'youtube':
      return {
        title: fields.title?.trim() || commonCaption.slice(0, YT_TITLE_MAX),
        visibility: fields.visibility,
        made_for_kids: !!fields.made_for_kids,
        tags: (fields.tags || '')
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      };
    case 'tiktok':
      return {
        privacy_level: fields.privacy_level,
        disable_comment: !!fields.disable_comment,
        disable_duet: !!fields.disable_duet,
        disable_stitch: !!fields.disable_stitch,
        brand_content_toggle: !!fields.brand_content_toggle,
        brand_organic_toggle: !!fields.brand_organic_toggle,
      };
    case 'instagram':
      return {
        share_to_feed: !!fields.share_to_feed,
        cover_frame_ms: fields.cover_frame_ms
          ? Number(fields.cover_frame_ms)
          : null,
      };
    case 'facebook':
      return { description: fields.description || null };
    default:
      return {};
  }
};

export default UploadVideoModal;
